import type { ReactNode } from 'react';
import {
  Description,
  FieldError,
  Input,
  Label,
  Select,
  TextArea,
  TextField,
} from '@heroui/react';
import { drawerFieldClassName } from './side-drawer-classes';

interface DrawerTextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  description?: ReactNode;
  errorMessage?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  autoComplete?: string;
}

export function DrawerTextField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  description,
  errorMessage,
  isInvalid,
  isDisabled,
  autoComplete,
}: DrawerTextFieldProps) {
  return (
    <TextField isInvalid={isInvalid} isDisabled={isDisabled}>
      <Label>{label}</Label>
      <Input
        className={drawerFieldClassName}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
      {description ? <Description>{description}</Description> : null}
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </TextField>
  );
}

interface DrawerTextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  description?: ReactNode;
  rows?: number;
  isDisabled?: boolean;
}

export function DrawerTextAreaField({
  label,
  value,
  onChange,
  placeholder,
  description,
  rows = 3,
  isDisabled,
}: DrawerTextAreaFieldProps) {
  return (
    <TextField isDisabled={isDisabled}>
      <Label>{label}</Label>
      <TextArea
        className={drawerFieldClassName}
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
      />
      {description ? <Description>{description}</Description> : null}
    </TextField>
  );
}

interface DrawerSelectFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  description?: ReactNode;
  children: ReactNode;
}

export function DrawerSelectField({
  label,
  value,
  onChange,
  isDisabled,
  isRequired,
  placeholder,
  description,
  children,
}: DrawerSelectFieldProps) {
  return (
    <Select
      placeholder={placeholder}
      value={value}
      onChange={(key) => onChange(key ? String(key) : '')}
      isDisabled={isDisabled}
      isRequired={isRequired}
    >
      <Label>{label}</Label>
      <Select.Trigger className={drawerFieldClassName}>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>{children}</Select.Popover>
      {description ? <Description>{description}</Description> : null}
    </Select>
  );
}
